import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Facebook, Instagram, Youtube } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import Picture from "@/components/ui/picture";

const Footer = () => {
  const { t } = useLanguage();

  return (
    <footer className="bg-foreground dark:bg-[hsl(215,25%,8%)] text-primary-foreground/80">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-12">
          {/* About */}
          <div>
            <Picture src="/images/logo_avps_official_dark.png" alt="AVPS Vimercate" width={520} height={354} className="h-28 md:h-36 w-auto mb-5" />
            <p className="text-sm leading-relaxed mb-5 text-primary-foreground/60">{t.footer.desc}</p>
            <div className="flex items-center gap-2">
              <a href="https://www.facebook.com/avpsvimercate/" target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="p-2.5 rounded-lg bg-primary-foreground/10 hover:bg-primary-foreground/20 transition-colors"><Facebook className="w-4 h-4" /></a>
              <a href="https://www.instagram.com/avps_vimercate/" target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="p-2.5 rounded-lg bg-primary-foreground/10 hover:bg-primary-foreground/20 transition-colors"><Instagram className="w-4 h-4" /></a>
              <a href="https://www.youtube.com/user/AVPSVimercate" target="_blank" rel="noopener noreferrer" aria-label="YouTube" className="p-2.5 rounded-lg bg-primary-foreground/10 hover:bg-primary-foreground/20 transition-colors"><Youtube className="w-4 h-4" /></a>
            </div>
          </div>

          {/* Links */}
          <div>
            <h3 className="text-primary-foreground font-heading font-semibold mb-4 text-sm uppercase tracking-wider">{t.footer.linkRapidi}</h3>
            <ul className="space-y-2.5 text-sm">
              <li><Link to="/chi-siamo" className="hover:text-primary-foreground transition-colors">{t.nav.chiSiamo}</Link></li>
              <li><Link to="/servizi" className="hover:text-primary-foreground transition-colors">{t.nav.servizi}</Link></li>
              <li><Link to="/diventa-volontario" className="hover:text-primary-foreground transition-colors font-semibold text-primary-foreground">{t.footer.diventaVol}</Link></li>
              <li><Link to="/servizi/corsi-formazione-sanitaria" className="hover:text-primary-foreground transition-colors font-semibold text-primary-foreground">{t.footer.nuoviCorsi}</Link></li>
              <li><Link to="/news" className="hover:text-primary-foreground transition-colors">{t.nav.newsEventi}</Link></li>
              <li><Link to="/sostienici" className="hover:text-primary-foreground transition-colors">{t.nav.sostienici}</Link></li>
              <li><Link to="/contatti" className="hover:text-primary-foreground transition-colors">{t.nav.contatti}</Link></li>
            </ul>
          </div>

          {/* Servizi */}
          <div>
            <h3 className="text-primary-foreground font-heading font-semibold mb-4 text-sm uppercase tracking-wider">{t.footer.serviziTitle}</h3>
            <ul className="space-y-2.5 text-sm">
              <li><Link to="/servizi/emergenza-sanitaria-112" className="hover:text-primary-foreground transition-colors">{t.nav.emergenza112}</Link></li>
              <li><Link to="/servizi/trasporto-sanitario-programmato" className="hover:text-primary-foreground transition-colors">{t.nav.trasportoSanitario}</Link></li>
              <li><Link to="/servizi/teleassistenza" className="hover:text-primary-foreground transition-colors">{t.nav.teleassistenza}</Link></li>
              <li><Link to="/servizi/corsi-formazione-sanitaria" className="hover:text-primary-foreground transition-colors">{t.nav.corsiFormazione}</Link></li>
              <li><Link to="/servizi/assistenza-manifestazioni-eventi" className="hover:text-primary-foreground transition-colors">{t.nav.assistenzaEventi}</Link></li>
            </ul>
          </div>

          {/* Contatti */}
          <div>
            <h3 className="text-primary-foreground font-heading font-semibold mb-4 text-sm uppercase tracking-wider">{t.footer.contattiTitle}</h3>
            <div className="space-y-3.5 text-sm">
              <a href="tel:0396612112" className="flex items-start gap-2.5 hover:text-primary-foreground transition-colors">
                <Phone className="w-4 h-4 mt-0.5 shrink-0" />
                <span>039 66 12 112<br /><span className="text-xs text-primary-foreground/50">{t.footer.serviziSecondari}</span><br /><span className="text-xs text-primary-foreground/50">{t.footer.emergenzaSoccorso}</span></span>
              </a>
              <a href="mailto:segreteria@avps.it" className="flex items-center gap-2.5 hover:text-primary-foreground transition-colors">
                <Mail className="w-4 h-4 shrink-0" /> segreteria@avps.it
              </a>
              <div className="flex items-start gap-2.5">
                <MapPin className="w-4 h-4 mt-0.5 shrink-0" />
                <span>Via Luigi Cadorna 28<br />20871 Vimercate (MB)</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="border-t border-primary-foreground/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex flex-col sm:flex-row items-center justify-between gap-2 text-xs text-primary-foreground/60">
          <p>{t.footer.copyright.replace("{year}", new Date().getFullYear().toString())}</p>
          <div className="flex items-center gap-4">
            <Link to="/chi-siamo/trasparenza" className="hover:text-primary-foreground/70 transition-colors">{t.nav.trasparenza}</Link>
            <Link to="/privacy" className="hover:text-primary-foreground/70 transition-colors">{t.footer.privacy}</Link>
            <Link to="/cookie-policy" className="hover:text-primary-foreground/70 transition-colors">{t.footer.cookie}</Link>
          </div>
        </div>
        <div className="border-t border-primary-foreground/5">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 text-center text-[11px] text-primary-foreground/40">
            {t.footer.creditVolontari}
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
